import { fetcher } from "@certn/api";
import { useCallback, useEffect, useRef, useState } from "react";

import { isTerminalKonfirStatus } from "../helpers";
import { KonfirVerificationResponse, KonfirVerificationStatus } from "../types";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const verificationsBaseUrl = (caseShortId: string) =>
  `/api/internal/applicant/case/${caseShortId}/konfir/verifications/`;

export function useKonfirVerification(caseShortId: string) {
  const [konfirVerificationId, setKonfirVerificationId] = useState<
    string | null
  >(null);
  const [status, setStatus] = useState<KonfirVerificationStatus | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(true);

  const pollIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const pollDeadlineRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = undefined;
    }
    pollDeadlineRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchExisting() {
      setIsStatusLoading(true);
      try {
        const response = await fetcher.request<KonfirVerificationResponse>(
          verificationsBaseUrl(caseShortId),
          { method: "GET" }
        );
        if (cancelled) return;
        if (response?.id) {
          setKonfirVerificationId(response.id);
          setStatus(response.status ?? null);
        }
      } catch (error) {
        console.error("Error fetching existing Konfir verification:", error);
      } finally {
        if (!cancelled) {
          setIsStatusLoading(false);
        }
      }
    }
    fetchExisting();
    return () => {
      cancelled = true;
    };
  }, [caseShortId]);

  const pollStatus = useCallback(
    async (verificationId: string) => {
      if (
        pollDeadlineRef.current !== null &&
        Date.now() >= pollDeadlineRef.current
      ) {
        stopPolling();
        return;
      }
      try {
        const response = await fetcher.request<KonfirVerificationResponse>(
          `${verificationsBaseUrl(caseShortId)}${verificationId}/`
        );
        if (response?.status) {
          setStatus(response.status);
          if (isTerminalKonfirStatus(response.status)) {
            stopPolling();
          }
        }
      } catch (error) {
        console.error("Error polling Konfir verification status:", error);
      }
    },
    [caseShortId, stopPolling]
  );

  const startVerification = useCallback(async () => {
    setIsStarting(true);
    try {
      const response = await fetcher.submit<KonfirVerificationResponse>(
        verificationsBaseUrl(caseShortId)
      );
      if (!response?.id || !response?.candidate_url) {
        return null;
      }
      setKonfirVerificationId(response.id);
      setStatus(response.status ?? "IN_PROGRESS");
      return response;
    } finally {
      setIsStarting(false);
    }
  }, [caseShortId]);

  useEffect(() => {
    if (!konfirVerificationId) return;
    if (isTerminalKonfirStatus(status)) return;

    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    pollIntervalRef.current = setInterval(() => {
      pollStatus(konfirVerificationId);
    }, POLL_INTERVAL_MS) as unknown as NodeJS.Timeout;

    return () => {
      stopPolling();
    };
  }, [konfirVerificationId, status, pollStatus, stopPolling]);

  return {
    konfirVerificationId,
    status,
    setStatus,
    isStarting,
    isStatusLoading,
    startVerification,
  };
}
